import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Field, FieldArray, formValueSelector, reduxForm } from 'redux-form';
import { MenuItem } from 'material-ui';
import { length, required } from 'redux-form-validators';
import { TextField, SelectField } from 'redux-form-material-ui';

import gmtData from 'data/gmt';
import { formName } from 'data/formNames';
import { getCountries } from 'actions/countries';

import Coordinates from './Coordinates';
import ErrorBoundary from '../../common/ErrorBoundary';
import ServiceProviders from './ServiceProviders';
import PortRestrictions from './PortRestrictions';
import Facilities from './Facilities';
import Warehouses from './Warehouses';
import LiftAndCranes from './LiftAndCranes/List';

const styles = {
    textField: {
        floatingLabelStyle: {
            fontSize: '12px',
        },
    },
    btn: {
        addServiceProvider: {
            style: {
                margin: '20px 0 70px 0',
            },
        },
        labelStyle: {
            color: '#fff', textTransform: 'none', fontSize: '13px',
        },
        style: {
            margin: '20px 0',
        },
    },
};

/**
 * @typedef {{
 *  handleSubmit: () => void,
 *  change: (name: string, value: any) => void,
 *  countries: any[],
 *  getCountries: () => void;
 *  customWarning: string | null;
 * }} Props
 * @type {import('react').FC<Props>}
 */
const PortAddForm = ({
    handleSubmit, change, countries, getCountries, customWarning,
}) => {
    useEffect(() => {
        getCountries();
    }, []);

    return (
        <form autoComplete='off' className='main add-port' onSubmit={handleSubmit}>
            <div className='main__title'>Coordinates</div>

            <Coordinates change={change} />

            <div className='main__title'>Main info</div>

            <div className='row'>
                <div className='col-xs-3'>
                    <Field
                        component={TextField}
                        name='name'
                        validate={[required(), length({ min: 3 })]}
                        floatingLabelText='Name'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        fullWidth
                    />
                </div>
                <div className='col-xs-3'>
                    <Field
                        component={SelectField}
                        name='country._id'
                        floatingLabelText='Country'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        autoWidth
                        fullWidth
                        validate={required()}
                        onChange={(e, value) => {
                            e.preventDefault();

                            change('country', countries.find(country => country._id === value));
                        }}
                    >
                        {
                            countries.map((country, key) => (
                                <MenuItem
                                    key={key}
                                    value={country._id}
                                    primaryText={country.name}
                                />
                            ))
                        }
                    </Field>
                </div>

                <div className='col-xs-3'>
                    <Field
                        component={CustomTextField}
                        name='unLoCode'
                        validate={[length({ max: 5, min: 5, allowBlank: true })]}
                        floatingLabelText='UN LOCODE'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        fullWidth
                        customWarning={customWarning}
                        normalize={value => (value ? value.toUpperCase() : '')}
                    />
                </div>

                <div className='col-xs-3'>
                    <Field
                        component={SelectField}
                        name='timezone'
                        floatingLabelText='timezone'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        fullWidth
                    >
                        {
                            gmtData.map((gmt, key) => (
                                <MenuItem
                                    key={key}
                                    value={gmt}
                                    primaryText={gmt}
                                />
                            ))
                        }
                    </Field>
                </div>

                <div className='col-xs-3'>
                    <Field
                        component={TextField}
                        name='site'
                        floatingLabelText='Web site'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        fullWidth
                        validate={[length({ min: 8, allowBlank: true })]}
                    />
                </div>
            </div>

            <div className='main__title'>SERVICE PROVIDERS</div>
            <ErrorBoundary>
                <FieldArray
                    name='serviceProviders'
                    component={ServiceProviders}
                    styles={styles}
                />
            </ErrorBoundary>
            <ErrorBoundary>
                <PortRestrictions styles={styles} />
            </ErrorBoundary>
            <ErrorBoundary>
                <LiftAndCranes />
            </ErrorBoundary>
            <ErrorBoundary>
                <Facilities />
            </ErrorBoundary>
            <ErrorBoundary>
                <Warehouses />
            </ErrorBoundary>
            <ErrorBoundary>
                <div className='main__title'>other</div>

                <div className='row'>
                    <div className='col-xs-12'>
                        <Field
                            name='additionalDescription'
                            component={TextField}
                            floatingLabelText='Other'
                            multiLine
                            fullWidth
                            floatingLabelStyle={styles.textField.floatingLabelStyle}
                        />
                    </div>
                </div>
            </ErrorBoundary>
        </form>
    );
};

const CustomTextField = ({
    input,
    label,
    meta: { touched, error },
    customWarning,
    ...custom
}) => {
    const hasError = touched && error;
    const hasWarning = !!customWarning;
    
    return (
        <div>
            <TextField
                {...input}
                {...custom}
                errorText={hasError ? error : hasWarning ? customWarning : null}
                errorStyle={hasWarning && !hasError ? { color: 'orange' } : { color: 'red' }}
            />
        </div>
    );
};

PortAddForm.displayName = 'PortAddForm';

const PortAddReduxForm = reduxForm({
    form: formName.portCreate,
    enableReinitialize: true,
    asyncBlurFields: ['unLoCode'],
})(/** @type {any} */ (PortAddForm)); // TODO: @types/redux-form should fix this

const selector = formValueSelector(formName.portCreate);

const getUnLoCodeWarning = (country, unLoCode) => {
    const shortNameCode = country ? country.shortNameCode : null;
    
    if (shortNameCode && unLoCode && !unLoCode.startsWith(shortNameCode)) {
        return `UN LOCODE should start with country code: ${shortNameCode}`;
    }
    return null;
};

export default connect(
    /** @param {any} state */
    (state) => {
        const { port, countries } = state;

        const country = selector(state, 'country');
        const unLoCode = selector(state, 'unLoCode');
        
        const customWarning = getUnLoCodeWarning(country, unLoCode);
        
        return {
            countries: countries.items,
            initialValues: { ...port },
            customWarning,
        };
    },
    dispatch => bindActionCreators({
        getCountries,
    }, dispatch),
)(PortAddReduxForm);



// WEBPACK FOOTER //
// ./app/components/Regions/PortAdd/PortAddForm.jsx